import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  Typography,
  Divider,
  Button,
  Tooltip,
  Popover,
  MenuItem,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Stack,
} from "@mui/material";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  DataGrid,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid";
import ExcelJS from "exceljs";
import CEQuickFilter from "components/CEQuickFilter";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import Grid from "@mui/material/Grid2";
import Menu from "@mui/material/Menu";
import { EditRounded } from "@mui/icons-material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { formatDate, normalizePhoneNumber } from "helpers/ServiceHelpers";
import api from "services/api";
import { NAVBAR_HEIGHT } from "constants/layout";
import { parsePhoneNumber } from "libphonenumber-js";
import { countryOptions } from "constants/countryData";

// Helper: format a phone number (national format, e.g. (555) 555-1234)
const formatPhoneNumber = (raw, countryCode) => {
  if (!raw) return "";
  try {
    // Extract extension (ext, x, etc.) before stripping digits
    let extension = null;
    const extMatch = String(raw).match(/(?:ext\.?|x|extension)\s*(\d+)/i);
    if (extMatch) {
      extension = extMatch[1];
    }
    // Check if the number already has an international prefix (+xxx)
    const intlMatch = String(raw).match(/^\+(\d{1,3})/);
    let cleaned = String(raw).replace(/[^\d]/g, "");

    let withPlus;
    if (intlMatch) {
      // Number already has country code prefix — use as-is
      const cc = intlMatch[1];
      cleaned = cleaned.slice(cc.length);
      withPlus = `+${cc}${cleaned}`;
    } else {
      // No international prefix — use the provided countryCode
      // Remove leading 1 if more than 10 digits (US country code)
      if (cleaned.length > 10 && cleaned.startsWith("1")) {
        cleaned = cleaned.slice(1);
      }
      // If still more than 10 digits, the rest is the extension
      if (cleaned.length > 10) {
        if (!extension) {
          extension = cleaned.slice(10);
        }
        cleaned = cleaned.slice(0, 10);
      }
      withPlus = `+${countryCode}${cleaned}`;
    }

    const phoneNumber = parsePhoneNumber(withPlus);
    if (phoneNumber && phoneNumber.isValid()) {
      let formatted = phoneNumber.formatNational();
      const cc = String(phoneNumber.countryCallingCode || "");
      // Strip leading trunk prefix (0) for non-US numbers
      if (cc && cc !== "1" && formatted.startsWith("0")) {
        formatted = formatted.slice(1);
      }
      // Include country code prefix for non-US numbers
      if (cc && cc !== "1") {
        formatted = `+${cc} ${formatted}`;
      }
      return extension ? `${formatted} x${extension}` : formatted;
    }
  } catch {
    // fall through
  }
  return raw;
};

const Clients = () => {
  usePageTitle("Clients");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const [listofClients, setListOfClients] = useState([]);
  const [listofClientsTypes, setListOfClientsTypes] = useState([]);
  const [listofClientServiceTypes, setListOfClientServiceTypes] = useState([]);
  const [listofContractStatuses, setListOfContractStatuses] = useState([]);
  const [listOfPaymentMethods, setListOfPaymentMethods] = useState([]);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);
  
  const { setOverlayData, setOverlayName } = useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();
  const { handleErrorDialog, openDialog, closeDialog } = useStateContext();

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Selection controls (match Translations list behavior)
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const longPressTriggeredRef = useRef(false);
  const LONG_PRESS_MS = 500;

  // Set up entity tracking for ClientsList to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to "ClientsList:all" so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "ClientsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Account" -> "CLIENTS_FILTER_ACCOUNT"
    const upperFieldName = fieldName.toUpperCase();
    return `CLIENTS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      // Remove from localStorage if no values selected
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback((columnFieldNames) => {
    const savedFilters = {};

    columnFieldNames.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback((columnFieldNames) => {
    columnFieldNames.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const CLIENTS_FILTER_KEY = "CLIENTS_FILTER_MODEL";

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CLIENTS_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    const secondData = {
      ClientID: selectedRow,
      Client: listofClients.find((linguist) => linguist.id === selectedRow),
      ClientTypes: listofClientsTypes,
      ContractStatuses: listofContractStatuses,
      ClientServiceTypes: listofClientServiceTypes,
      PaymentMethods: listOfPaymentMethods,
      Action: OVERLAY_NAMES.CLIENT_ACTION_EDIT,
    };

    setOverlayData(secondData);

    setOverlayName(OVERLAY_NAMES.CLIENT_NEW_EDIT);

    setContextMenu(null);
  };

  const getMainRequestor = (requestors) => {
    if (!requestors || requestors.length === 0) return null;

    const mainRequestor = requestors.find((requestor) => requestor.IsMain);

    if (mainRequestor) {
      return mainRequestor;
    }

    if (requestors.length === 1) {
      return requestors[0];
    }

    return null;
  };

  const getMainRequestorPhoneNumber = (requestors) => {
    const requestor = getMainRequestor(requestors);
    if (!requestor) return null;
    const phones = requestor.PhoneNumbers || [];
    const primary = phones.find((p) => p.IsPrimary) || phones[0];
    if (primary && primary.Number) {
      const cc = primary.CountryCode || "1";
      const formatted = formatPhoneNumber(primary.Number, cc);
      const ext = primary.Ext ? ` x${primary.Ext}` : "";
      return `${formatted}${ext}`;
    }
    return requestor.PhoneNumber ? formatPhoneNumber(requestor.PhoneNumber, "1") : null;
  };

  const getMainRequestorCallLink = (requestors) => {
    const requestor = getMainRequestor(requestors);
    if (!requestor) return null;
    const phones = requestor.PhoneNumbers || [];
    const primary = phones.find((p) => p.IsPrimary) || phones[0];
    const cc = primary?.CountryCode || "1";
    const phone = String(primary?.Number || requestor?.PhoneNumber || "").trim();
    const ext = String(primary?.Ext || requestor?.Ext || "").trim();

    if (!phone) return null;
    // Include country code in the tel: link
    const dialable = `+${cc}${phone.replace(/[^0-9]/g, "")}`;
    if (!ext) return `tel:${dialable}`;
    return `tel:${dialable},,${ext}`;
  };

  const handleContextMenuCall = () => {
    const selectedClient = listofClients.find(
      (client) => client.id === selectedRow,
    );
    if (selectedClient) {
      const callLink = getMainRequestorCallLink(selectedClient.Requestors);
      if (callLink) {
        window.location.href = callLink;
      } else {
        showSnackbar(
          "No Phone Number Available for this Client.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendEmail = () => {
    const selectedLinguist = listofClients.find(
      (client) => client.id === selectedRow,
    );
    if (selectedLinguist) {
      const email = selectedLinguist.Email;
      if (email) {
        window.open(`mailto:${email}`, "_blank");
      } else {
        showSnackbar("No Email Available for this Client.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuSendSMS = () => {
    const selectedClient = listofClients.find(
      (client) => client.id === selectedRow,
    );
    if (selectedClient) {
      const phoneNumber = getMainRequestorPhoneNumber(
        selectedClient.Requestors,
      );
      if (phoneNumber) {
        window.location.href = `sms:${phoneNumber}`;
      } else {
        showSnackbar(
          "No Phone Number Available for this Client.",
          theme,
          "error",
        );
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuOpenMaps = () => {
    const client = listofClients.find(({ id }) => id === selectedRow);

    if (client) {
      if (client.MainAddress) {
        const encodedAddress = encodeURIComponent(client.MainAddress);
        window.open(
          `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
          "_blank",
        );
      } else {
        showSnackbar("No Address Available for this Client.", theme, "error");
      }
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyEmail = () => {
    const client = listofClients.find(({ id }) => id === selectedRow);
    if (client && client.Requestors && client.Requestors.length > 0) {
      const email = client.Requestors[0].Email;
      if (email) {
        navigator.clipboard
          .writeText(email)
          .then(() =>
            showSnackbar(
              "Email successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar("Failed to copy email to clipboard.", theme, "error"),
          );
      } else {
        showSnackbar("No Email Available for this Client.", theme, "error");
      }
    } else {
      showSnackbar("No Email Available for this Client.", theme, "error");
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyNumber = () => {
    const selectedClient = listofClients.find(
      (client) => client.id === selectedRow,
    );
    if (
      selectedClient &&
      selectedClient.Requestors &&
      selectedClient.Requestors.length > 0
    ) {
      const phoneNumber = getMainRequestorPhoneNumber(
        selectedClient.Requestors,
      );
      if (phoneNumber) {
        navigator.clipboard
          .writeText(phoneNumber)
          .then(() => {
            showSnackbar(
              "Phone number successfully copied to clipboard.",
              theme,
              "success",
            );
          })
          .catch(() => {
            showSnackbar(
              "Failed to copy phone number to clipboard.",
              theme,
              "error",
            );
          });
      } else {
        showSnackbar(
          "No Phone Number Available for this Client.",
          theme,
          "error",
        );
      }
    } else {
      showSnackbar(
        "No Phone Number Available for this Client.",
        theme,
        "error",
      );
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyAddress = () => {
    const client = listofClients.find(({ id }) => id === selectedRow);
    if (client) {
      const address = client.MainAddress;
      if (address) {
        navigator.clipboard
          .writeText(address)
          .then(() =>
            showSnackbar(
              "Address successfully copied to clipboard.",
              theme,
              "success",
            ),
          )
          .catch(() =>
            showSnackbar(
              "Failed to copy address to clipboard.",
              theme,
              "error",
            ),
          );
      } else {
        showSnackbar("No Address Available for this Client.", theme, "error");
      }
    } else {
      showSnackbar("No Address Available for this Client.", theme, "error");
    }
    setContextMenu(null);
  };

  const handleContextMenuAddTask = () => {
    const clientData = listofClients.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Client",
        EntityID: selectedRow,
      },
      Client: {
        id: clientData.id,
        Account: clientData.Account,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      longPressTriggeredRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      longPressTriggeredRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
    setTimeout(() => {
      longPressTriggeredRef.current = false;
    }, 0);
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      // Keep only last selected when not in selection mode.
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = () => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressNextClickRef.current = false;
  };

  const handleMergeClients = () => {
    if (selectionModel.length < 1) {
      showSnackbar("Select at least 1 client to merge.", theme, "error");
      return;
    }

    setOverlayData({
      Clients: listofClients,
      SelectedClientIds: selectionModel,
    });

    setOverlayName(OVERLAY_NAMES.CLIENT_MERGE);
  };

  const getSelectedClientData = () => {
    return listofClients.find(({ id }) => id === selectedRow);
  };

  const hasEmail = () => {
    const client = getSelectedClientData();
    return client?.Requestors?.[0]?.Email;
  };

  const hasPhoneNumber = () => {
    const client = getSelectedClientData();
    return getMainRequestorPhoneNumber(client?.Requestors);
  };

  const hasAddress = () => {
    const client = getSelectedClientData();
    return client?.MainAddress;
  };

  // Fetch clients list data
  const fetchClientsData = useCallback(() => {
    api
      .all([
        api.get("/clients"),
        api.get("/clients/clientTypes"),
        api.get("/clients/contractStatuses"),
        api.get("/clients/clientServiceTypes"),
        api.get("/clients/paymentMethods"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            clientTypesResponse,
            contractStatusesResponse,
            clientServiceTypesResponse,
            paymentMethodsResponse,
          ) => {
            if (clientsResponse !== null) {
              const clients = clientsResponse.data.map((client) => {
                if (client.ClientContractHistories) {
                  client.ClientContractHistories.reverse();
                }
                return client;
              });
              setListOfClients(clients);
            }

            if (clientTypesResponse !== null) {
              setListOfClientsTypes(clientTypesResponse.data);
            }

            if (contractStatusesResponse !== null) {
              setListOfContractStatuses(contractStatusesResponse.data);
            }

            if (clientServiceTypesResponse !== null) {
              setListOfClientServiceTypes(clientServiceTypesResponse.data);
            }

            if (paymentMethodsResponse !== null) {
              setListOfPaymentMethods(paymentMethodsResponse.data);
            }

            setIsLoading(false);
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsDataListsReady(true); // Even on error, mark as ready to prevent infinite waiting
      });
  }, [handleErrorDialog, theme]);

  useEffect(() => {
    fetchClientsData();
  }, [refreshPage, fetchClientsData]);

  // Listen for client updates from other members
  const handleClientsListRefetch = useCallback(
    (updatedBy) => {
      // Reload the clients list data
      fetchClientsData();
    },
    [fetchClientsData],
  );

  useAssignmentUpdates({
    modelType: "ClientsList",
    recordId: "all",
    onUpdate: handleClientsListRefetch,
  });

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      setOverlayData({
        ClientTypes: listofClientsTypes,
        ContractStatuses: listofContractStatuses,
        ClientServiceTypes: listofClientServiceTypes,
        PaymentMethods: listOfPaymentMethods,
        Action: OVERLAY_NAMES.CLIENT_ACTION_NEW,
      });

      setOverlayName(OVERLAY_NAMES.CLIENT_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listofClientsTypes,
    listofContractStatuses,
    listofClientServiceTypes,
    listOfPaymentMethods,
    setOverlayData,
    setOverlayName,
  ]);

  const columns = [
    {
      field: "Account",
      headerName: "Account",
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestors[0].Name",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestors;
        const value = data && data[0] ? data[0].Name : "N/A";
        return value;
      },
      minWidth: 160,
      flex: 1.6,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[1] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestors[0].PhoneNumber",
      headerName: "Phone Number",
      valueGetter: (_, params) => {
        const data = params.Requestors;
        const value =
          data && data[0]
            ? data[0].PhoneNumber != null
              ? data[0].PhoneNumber
              : "N/A"
            : "N/A";
        return normalizePhoneNumber(value);
      },
      minWidth: 160,
      flex: 1.6,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[2] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestors[0].Email",
      headerName: "Email",
      valueGetter: (_, params) => {
        const data = params.Requestors;
        const value =
          data && data[0]
            ? data[0].Email != null
              ? data[0].Email
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 220,
      flex: 2.2,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[3] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "MainAddress",
      headerName: "Address",
      minWidth: 280,
      flex: 2.8,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Type",
      headerName: "Type",
      minWidth: 150,
      flex: 1.5,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[5] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ClientContractHistories.Status",
      headerName: "Contract Status",
      valueGetter: (_, params) => {
        const data = params.ClientContractHistories;
        const value =
          data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 165,
      flex: 1.65,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[6] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ClientContractHistories.Date",
      headerName: "Contract Date",
      valueGetter: (_, params) => {
        const data = params.ClientContractHistories;
        const value =
          data && data[0]
            ? data[0].Date != null
              ? formatDate(data[0].Date)
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[7] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ClientContractHistories.RenewDate",
      headerName: "Contract Renew Date",
      valueGetter: (_, params) => {
        const data = params.ClientContractHistories;
        const value =
          data && data[0]
            ? data[0].RenewDate != null
              ? formatDate(data[0].RenewDate)
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 200,
      flex: 2.0,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[8] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ClientContractHistories.RenewalDueDate",
      headerName: "Contract Renewal Due Date",
      valueGetter: (_, params) => {
        const data = params.ClientContractHistories;
        const value =
          data && data[0]
            ? data[0].RenewalDueDate != null
              ? data[0].RenewalDueDate
              : "N/A"
            : "N/A";
        return value;
      },
      minWidth: 250,
      flex: 2.5,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={filterData[9] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Note",
      headerName: "Note",
      minWidth: 290,
      flex: 2.9,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[10] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.In-Person",
      headerName: "In-Person",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 1) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const data = params.row.ClientServiceTypes;
        const hasService =
          data && data.some((item) => item.ServiceType.id === 1);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {hasService ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 130,
      flex: 1.3,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[11] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.OPI",
      headerName: "OPI",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 2) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const data = params.row.ClientServiceTypes;
        const hasService =
          data && data.some((item) => item.ServiceType.id === 2);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {hasService ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 100,
      flex: 1,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[12] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.Training",
      headerName: "Training",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 3) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const data = params.row.ClientServiceTypes;
        const hasService =
          data && data.some((item) => item.ServiceType.id === 3);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {hasService ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 140,
      flex: 1.4,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[13] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.Consulting",
      headerName: "Consulting",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 4) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const data = params.row.ClientServiceTypes;
        const hasService =
          data && data.some((item) => item.ServiceType.id === 4);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {hasService ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 140,
      flex: 1.4,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[14] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "ServiceType.Translation",
      headerName: "Translation",
      valueGetter: (_, params) => {
        const data = params.ClientServiceTypes;
        const value =
          data && data.some((item) => item.ServiceType.id === 5) ? "Yes" : "No";
        return value;
      },
      renderCell: (params) => {
        const data = params.row.ClientServiceTypes;
        const hasService =
          data && data.some((item) => item.ServiceType.id === 5);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {hasService ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 140,
      flex: 1.4,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[15] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  // Grid management state
  const getSavedDensity = () => {
    const saved = localStorage.getItem("CLIENTS_GRID_DENSITY");
    return saved || "standard";
  };

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("CLIENTS_COLUMNS_VISIBLE");
    return saved ? JSON.parse(saved) : {};
  };

  const [density, setDensity] = useState(getSavedDensity);
  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);
  const apiRef = useGridApiRef();

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'
  const [overlayAnchorEl, setOverlayAnchorEl] = useState(null);

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("CLIENTS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "CLIENTS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState(getStoredFilterModel);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listofClients || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listofClients || listofClients.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const columnFieldNames = columns.map((col) => col.field);
    const savedFilters = loadAllColumnFilters(columnFieldNames);
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listofClients, loadAllColumnFilters, columns]); // Apply saved filters once per loaded dataset

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters(columns.map((col) => col.field));
    // Clear quick filter
    setFilterModel({ items: [] });
    localStorage.setItem(CLIENTS_FILTER_KEY, JSON.stringify({ items: [] }));
  }, [clearAllFilters, clearAllColumnFilters]);

  // Quick filter handling
  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(CLIENTS_FILTER_KEY, JSON.stringify(newFilterModel));
  };

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      { field: "Account", valueGetter: (row) => row.Account || "" },
      {
        field: "Requestors[0].Name",
        valueGetter: (row) => {
          const data = row.Requestors;
          return data && data[0] ? data[0].Name : "N/A";
        },
      },
      {
        field: "Requestors[0].PhoneNumber",
        valueGetter: (row) => {
          const data = row.Requestors;
          const value =
            data && data[0]
              ? data[0].PhoneNumber != null
                ? data[0].PhoneNumber
                : "N/A"
              : "N/A";
          return normalizePhoneNumber(value);
        },
      },
      {
        field: "Requestors[0].Email",
        valueGetter: (row) => {
          const data = row.Requestors;
          return data && data[0]
            ? data[0].Email != null
              ? data[0].Email
              : "N/A"
            : "N/A";
        },
      },
      { field: "MainAddress", valueGetter: (row) => row.MainAddress || "" },
      { field: "Type", valueGetter: (row) => row.Type || "" },
      {
        field: "ClientContractHistories.Status",
        valueGetter: (row) => {
          const data = row.ClientContractHistories;
          return data && data[0]
            ? data[0].Status != null
              ? data[0].Status
              : "N/A"
            : "N/A";
        },
      },
      {
        field: "ClientContractHistories.Date",
        valueGetter: (row) => {
          const data = row.ClientContractHistories;
          return data && data[0]
            ? data[0].Date != null
              ? formatDate(data[0].Date)
              : "N/A"
            : "N/A";
        },
      },
      {
        field: "ClientContractHistories.RenewDate",
        valueGetter: (row) => {
          const data = row.ClientContractHistories;
          return data && data[0]
            ? data[0].RenewDate != null
              ? formatDate(data[0].RenewDate)
              : "N/A"
            : "N/A";
        },
      },
      {
        field: "ClientContractHistories.RenewalDueDate",
        valueGetter: (row) => {
          const data = row.ClientContractHistories;
          return data && data[0]
            ? data[0].RenewalDueDate != null
              ? data[0].RenewalDueDate
              : "N/A"
            : "N/A";
        },
      },
      { field: "Note", valueGetter: (row) => row.Note || "" },
      {
        field: "ServiceType.In-Person",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 1)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.OPI",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 2)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.Training",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 3)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.Consulting",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 4)
            ? "Yes"
            : "No";
        },
      },
      {
        field: "ServiceType.Translation",
        valueGetter: (row) => {
          const data = row.ClientServiceTypes;
          return data && data.some((item) => item.ServiceType.id === 5)
            ? "Yes"
            : "No";
        },
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listofClients || [];
      }

      return (listofClients || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listofClients, filterColumns]);

  // Filter columns based on visibility preferences
  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const handleSortModelChange = (newSortModel) => {
    //setSortModel(newSortModel);
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={!columns.some((col) => columnsVisible[col.field] === false)}
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Helper to get data filtered by both column filters AND quick filter
  const getExportData = useCallback(() => {
    const allColumns = columns || [];
    const visibleColumns =
      apiRef.current?.getVisibleColumns?.() ??
      allColumns.filter((col) => columnsVisible[col.field] !== false);

    if (visibleColumns.length === 0) {
      return { visibleColumns: [], data: [] };
    }

    // Get row IDs that pass ALL filters (column filters + quick filter + sorting)
    const filteredSortedRowIds = apiRef.current
      ? gridFilteredSortedRowIdsSelector(apiRef)
      : [];
    const rowMap = new Map(
      (columnFilteredData || []).map((row) => [row.id, row]),
    );
    const data = filteredSortedRowIds
      .map((id) => rowMap.get(id))
      .filter(Boolean);

    return { visibleColumns, data };
  }, [columns, columnsVisible, columnFilteredData, apiRef]);

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const { visibleColumns, data: exportData } = getExportData();

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!exportData || exportData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No clients data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              const csvRows = exportData
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.valueGetter) {
                            value = col.valueGetter(null, row);
                          } else if (col.field) {
                            value = row[col.field];
                          }
                          const strValue = String(value ?? "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");
              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `clients-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const { visibleColumns, data: exportData } = getExportData();

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!exportData || exportData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No clients data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Create workbook and worksheet
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Clients");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              exportData.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.valueGetter) {
                      value = col.valueGetter(null, row);
                    } else if (col.field) {
                      value = row[col.field];
                    }
                    const strValue = String(value ?? "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `clients-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, theme, isMobileToolbar }) => {
      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {selectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleMergeClients}
                disabled={selectionModel.length === 0}
                startIcon={<MergeRoundedIcon />}
                sx={{
                  mr: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Merge Clients
              </Button>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {hasAnyActiveFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && hasAnyActiveFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobileToolbar} />

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: theme.palette.grey.main,
                  borderColor: theme.palette.divider,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  setOverlayAnchorEl(optionsAnchorEl);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openDialog({
                    title: "Export Data",
                    theme: theme,
                    customContent: <ExportDialogContent />,
                  });
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    selectionEnabled,
    selectionModel,
    handleCancelSelection,
    handleMergeClients,
    openDialog,
  ]);

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ pr: 1 }}
          >
            {!isMobile ? (
              <Button
                onClick={() => {
                  setPendingCreateNew(true);
                }}
                startIcon={<AddRoundedIcon />}
                sx={{
                  width: "200px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 16px",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  boxShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                  color: theme.palette.background.default,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "& .MuiButton-startIcon": {
                    marginRight: 0,
                  },
                  "& .MuiButton-endIcon": {
                    display: "none",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                    boxShadow:
                      "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box sx={{ flex: 1, textAlign: "center" }}>Add Client</Box>
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setPendingCreateNew(true);
                }}
                sx={{
                  width: "40px",
                  minWidth: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  boxShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                  color: theme.palette.background.default,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "& .MuiButton-startIcon": {
                    margin: 0,
                  },
                  "& .MuiButton-endIcon": {
                    display: "none",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                    boxShadow:
                      "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <AddRoundedIcon />
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          mb: 1,
          mx: 1,
          mt: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: theme.shadows[3],
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          columns={filteredColumns}
          apiRef={apiRef}
          density={density}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          disableColumnMenu
          disableColumnSorting
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            if (selectionEnabled) {
              if (suppressNextClickRef.current) {
                event.defaultMuiPrevented = true;
                suppressNextClickRef.current = false;
              }
              return;
            }

            if (longPressTriggeredRef.current) {
              return;
            }

            setSelectionModel([params.id]);
          }}
          onRowDoubleClick={(params) => {
            if (openFilter || selectionEnabled || longPressTriggeredRef.current) {
              return;
            }

            const data = {
              Client: params.row,
              ClientID: params.row.id,
            };

            setOverlayData(data);
            navigate(`/clients/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "No" || params.value === "N/A"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              isMobileToolbar: isMobile,
              selectionEnabled,
              selectionModel,
              onCancelSelection: handleCancelSelection,
              onMerge: handleMergeClients,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },

            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
          <MenuItem onClick={handleContextMenuAddTask}>
            <ListItemIcon>
              <AddTaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Add Task</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCall}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <CallRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Call</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuSendSMS}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <SmsRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send SMS</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuOpenMaps}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <LocationOnRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Open Maps</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCopyEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Email</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyNumber}
            disabled={!hasPhoneNumber()}
            sx={{
              opacity: hasPhoneNumber() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Number</Typography>
          </MenuItem>
          <MenuItem
            onClick={handleContextMenuCopyAddress}
            disabled={!hasAddress()}
            sx={{
              opacity: hasAddress() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Address</Typography>
          </MenuItem>
        </Menu>
      </Box>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={
            overlayAnchorEl
              ? { left: window.innerWidth / 2, top: window.innerHeight / 2 }
              : undefined
          }
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              maxWidth: "calc(100vw - 2rem)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Clients;
